/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect } from "react";
import { Lightbulb, Zap, Clock, Monitor, Check } from "lucide-react";
import { useGetLicenses } from '../../functions/licenseHook';

interface License {
  readonly _id: string;
  readonly name: string;
  readonly description: string;
  readonly price: number;
  readonly validity: string;
  readonly amountOfSites: number;
  readonly commercialUse: boolean;
}

interface LicenseFeature {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

interface LicenseSelectorProps {
  selectedLicense?: string | null;
  onSelect: (licenseId: string) => void;
}


const LicenseSelector: React.FC<LicenseSelectorProps> = ({ selectedLicense, onSelect }) => {
  const { licenses, isLoading, isError, error } = useGetLicenses();

  useEffect(() => {
    if (!selectedLicense && licenses.length > 0) {
      const defaultLicense = licenses.find((license: { name: string; }) => license.name === "Pro License");
      defaultLicense && onSelect(defaultLicense._id);
    }
  }, [licenses, selectedLicense, onSelect]);

  if (isLoading) return <LicenseGridSkeleton />;
  if (isError) return <ErrorDisplay error={error} />;

  return (
    <div className="px-4 md:px-6">
      <HeaderSection />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {licenses.map((license) => (
          <LicenseCard
            key={license._id}
            license={license}
            isSelected={selectedLicense === license._id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

// Sub-components
const HeaderSection: React.FC = () => (
  <div className="mb-8 text-center">
    <div className="max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">
        Choose the License You Need
      </h2>
      <p className="text-lg text-gray-600 mb-6">
        Select the option that best fits your project requirements
      </p>
      <div className="h-1 w-24 bg-blue-600 mx-auto rounded-full" />
    </div>
  </div>
);

const LicenseCard: React.FC<{
  license: License;
  isSelected: boolean;
  onSelect: (id: string) => void;
}> = ({ license, isSelected, onSelect }) => {
  const features: LicenseFeature[] = [
   
    {
      icon: <Monitor className="h-5 w-5 text-blue-600" />,
      label: "Sites",
      value: `${license.amountOfSites}`
    },
   
  ];

  return (
    <div
      role="button"
      onClick={() => onSelect(license._id)}
      className={`relative p-4 border rounded-xl cursor-pointer transition-all
        ${isSelected 
          ? "border-blue-600 bg-blue-50 shadow-lg" 
          : "border-gray-200 bg-white hover:border-blue-400 hover:shadow-md"}
        group focus:outline-none focus:ring-2 focus:ring-blue-500`}
      tabIndex={0}
    >
      {isSelected && (
        <div className="absolute top-3 right-3 bg-blue-600 p-1.5 rounded-full">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}

      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Lightbulb className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{license.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{license.description}</p>
        </div>
      </div>

      <div className="mb-4">
        <span className="text-2xl font-bold text-gray-900">
          ${license.price}
        </span>
        <span className="text-sm text-gray-500 ml-2">/license</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        {features.map(({ icon, label, value }) => (
          <div key={label} className="flex items-center gap-2 text-gray-600">
            {icon}
            <span className="font-medium">{label}:</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LicenseGridSkeleton: React.FC = () => (
  <div className="px-4 md:px-6">
    <HeaderSection />
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
      {[1, 2].map((i) => (
        <div key={i} className="p-4 border rounded-xl bg-white">
          <div className="animate-pulse space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 bg-gray-200 rounded-lg" />
              <div className="space-y-2 flex-1">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
              </div>
            </div>
            
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="flex items-center gap-2">
                  <div className="h-5 w-5 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ErrorDisplay: React.FC<{ error?: Error }> = ({ error }) => (
  <div className="max-w-2xl mx-auto p-4 text-center">
    <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-100">
      <h3 className="font-medium mb-2">Error loading licenses</h3>
      <p>{error?.message || "Please try again later"}</p>
    </div>
  </div>
);

export default LicenseSelector;